import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ROLE_ADMIN = 3;
const ROLE_OFFICIAL = 6;
const ROLE_EXAMINER = 4;
const ROLE_INSTRUCTOR = 5;
const ROLE_MEMBER = 1;

interface NavItem {
  label: string;
  path: string;
  icon: string;
  roles: number[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: '📊', roles: [ROLE_ADMIN, ROLE_OFFICIAL, ROLE_EXAMINER, ROLE_INSTRUCTOR, ROLE_MEMBER] },
  { label: 'Members', path: '/reports/members', icon: '👥', roles: [ROLE_ADMIN, ROLE_OFFICIAL, ROLE_EXAMINER, ROLE_INSTRUCTOR] },
  { label: 'Dues & Payments', path: '/reports/memberships', icon: '💳', roles: [ROLE_ADMIN, ROLE_OFFICIAL, ROLE_INSTRUCTOR] },
  { label: 'Grading Results', path: '/reports/gradings', icon: '🥋', roles: [ROLE_ADMIN, ROLE_OFFICIAL, ROLE_EXAMINER, ROLE_INSTRUCTOR] },
  { label: 'Eligibility', path: '/reports/eligibility', icon: '✅', roles: [ROLE_ADMIN, ROLE_OFFICIAL, ROLE_EXAMINER, ROLE_INSTRUCTOR] },
  { label: 'Registration', path: '/reports/registration', icon: '📋', roles: [ROLE_ADMIN, ROLE_OFFICIAL, ROLE_EXAMINER, ROLE_INSTRUCTOR] },
  { label: 'My Payments', path: '/reports/my-payments', icon: '💰', roles: [ROLE_MEMBER, ROLE_INSTRUCTOR, ROLE_ADMIN] },
  { label: 'My Gradings', path: '/reports/my-gradings', icon: '🏅', roles: [ROLE_MEMBER, ROLE_INSTRUCTOR, ROLE_ADMIN] },
];

const getRoleName = (roleId?: number): string => {
  const roles: { [key: number]: string } = {
    1: 'Member', 2: 'Applicant', 3: 'Administrator',
    4: 'Examiner', 5: 'Instructor', 6: 'Official',
  };
  return roleId ? roles[roleId] || 'Unknown' : '';
};

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) setIsOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const visibleItems = navItems.filter(
    (item) => user && item.roles.includes(user.roleId)
  );

  return (
    <>
      {/* Inject responsive CSS */}
      <style>{`
        @media (max-width: 767px) {
          .nask-main-content {
            margin-left: 0 !important;
            margin-top: 56px !important;
          }
        }
      `}</style>

      {/* Mobile top bar */}
      {isMobile && (
        <div style={styles.mobileTopBar}>
          <button onClick={() => setIsOpen(true)} style={styles.hamburger}>☰</button>
          <div style={styles.mobileTitle}>
            <span style={{ fontSize: '20px' }}>⛩️</span>
            <span style={styles.logoTitle}>NASK</span>
          </div>
          <div style={styles.mobileAvatar}>
            {user?.otherNames?.charAt(0)}{user?.lastName?.charAt(0)}
          </div>
        </div>
      )}

      {/* Overlay */}
      {isMobile && isOpen && (
        <div onClick={() => setIsOpen(false)} style={styles.overlay} />
      )}

      {/* Sidebar panel */}
      <div style={{
        ...styles.sidebar,
        transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
        transition: 'transform 0.25s ease',
        zIndex: isMobile ? 1001 : 100,
        top: isMobile ? '0' : '0',
        boxShadow: isMobile && isOpen ? '4px 0 24px rgba(0,0,0,0.6)' : 'none',
      }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>⛩️</div>
          <div style={styles.logoText}>
            <span style={styles.logoTitle}>NASK</span>
            <span style={styles.logoSubtitle}>Reports</span>
          </div>
          {isMobile && (
            <button onClick={() => setIsOpen(false)} style={styles.closeBtn}>✕</button>
          )}
        </div>

        {/* User Info */}
        <div style={styles.userInfo}>
          <div style={styles.userAvatar}>
            {user?.otherNames?.charAt(0)}{user?.lastName?.charAt(0)}
          </div>
          <div style={styles.userDetails}>
            <span style={styles.userName}>{user?.otherNames} {user?.lastName}</span>
            <span style={styles.userRole}>{getRoleName(user?.roleId)}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav style={styles.nav}>
          {visibleItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={isActive ? styles.navItemActive : styles.navItem}
              >
                <span style={styles.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <button onClick={handleLogout} style={styles.logoutButton}>
          <span>🚪</span>
          <span>Sign Out</span>
        </button>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  sidebar: {
    width: '240px',
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    borderRight: '1px solid #2a2a2a',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 20px',
    borderBottom: '1px solid #2a2a2a',
  },
  logo: { fontSize: '28px' },
  logoText: { display: 'flex', flexDirection: 'column', flex: 1 },
  logoTitle: { color: '#ffffff', fontWeight: '700', fontSize: '18px', letterSpacing: '3px' },
  logoSubtitle: { color: '#888888', fontSize: '11px', letterSpacing: '1px' },
  closeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#888888',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    marginLeft: 'auto',
    lineHeight: 1,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    borderBottom: '1px solid #2a2a2a',
    backgroundColor: '#222222',
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#cc0000',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  userDetails: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  userName: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userRole: { color: '#cc0000', fontSize: '11px', fontWeight: '500' },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 12px',
    gap: '4px',
    flex: 1,
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 12px', borderRadius: '8px', border: 'none',
    backgroundColor: 'transparent', color: '#888888',
    fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%',
  },
  navItemActive: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 12px', borderRadius: '8px', border: 'none',
    backgroundColor: '#2a0000', color: '#ffffff',
    fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%',
  },
  navIcon: { fontSize: '16px', width: '20px', textAlign: 'center' },
  logoutButton: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '16px 20px', border: 'none',
    borderTop: '1px solid #2a2a2a', backgroundColor: 'transparent',
    color: '#666666', fontSize: '14px', cursor: 'pointer', width: '100%',
  },
  mobileTopBar: {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    height: '56px',
    backgroundColor: '#1a1a1a',
    borderBottom: '1px solid #2a2a2a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    zIndex: 998,
  },
  hamburger: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ffffff',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
  },
  mobileTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  mobileAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#cc0000',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 1000,
  },
};

export default Sidebar;